import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Check, AlertCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // 60 seconds total

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  // Support both Razorpay (subscription_id) and legacy Stripe (session_id) links.
  const subscriptionId = params.get("subscription_id");
  const sessionId = params.get("session_id");
  const pollsRef = useRef(0);
  const [status, setStatus] = useState("pending"); // pending | paid | expired | failed
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!subscriptionId && !sessionId) {
      setErr("Missing payment reference. Return to subscribe.");
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const isRazorpay = !!subscriptionId;
    const statusUrl = isRazorpay
      ? `/billing/razorpay/status/${subscriptionId}`
      : `/billing/status/${sessionId}`;

    const tick = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const { data } = await api.get(statusUrl);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        // Razorpay: 'cancelled' | 'expired' both terminal
        const s = (data.status || "").toLowerCase();
        if (s === "expired" || s === "cancelled") {
          setStatus("expired");
          return;
        }
        if (pollsRef.current >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not verify payment.");
        setStatus("failed");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [subscriptionId, sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-sg-paper">
      <header className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/inpropportal-logo.png" alt="INPropPortal.com" className="h-11 w-auto" />
          </Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="billing-success-root">
        {status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-sg-gold animate-spin mx-auto" />
            <h1 className="mt-6 font-display text-4xl text-sg-navy">Confirming your payment…</h1>
            <p className="mt-3 text-sg-mute">This typically takes 5–10 seconds. Please don&apos;t close this page.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="mt-6 font-display text-5xl text-sg-navy" data-testid="billing-paid-title">You&apos;re in.</h1>
            <p className="mt-3 text-sg-mute">Subscription activated. Next: set up your agent profile and branded portal.</p>
            <Button
              size="lg"
              onClick={() => navigate(user?.onboarded ? "/dashboard" : "/onboarding")}
              className="mt-8 h-12 rounded-full bg-sg-navy text-white hover:bg-sg-navy-700 px-8"
              data-testid="billing-continue"
            >
              Continue to setup <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "failed") && (
          <>
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="mt-6 font-display text-4xl text-sg-navy">
              {status === "expired" ? "Checkout expired" : status === "timeout" ? "Still confirming…" : "Verification failed"}
            </h1>
            <p className="mt-3 text-sg-mute">
              {err || "If the amount has been debited, please refresh in a minute. Otherwise, restart checkout."}
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full" data-testid="billing-retry">Refresh status</Button>
              <Button onClick={() => navigate("/subscribe")} className="rounded-full bg-sg-navy text-white" data-testid="billing-back-to-subscribe">Back to plan</Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
